<template>
  <div :key="product?.updateTime" class="product" :class="[{ skeleton: !product }]">
    <template v-if="product && !showPlaceholder && properties">
      <RibbonComponent
        v-if="!store.showDiscreetAvailabilityStatuses"
        :colour="product.availability?.colour"
        :text="translate(product.availability?.label, 'terms')"
        position="right"
      />
      <router-link
        :to="{
          name: 'product',
          state: { modal: true },
          params: {
            sku: product.sku,
          },
        }"
        class="position-relative"
      >
        <!-- <RibbonComponent :colour="product.availability.colour" :text="translate(product.availability.label, 'terms')" position="left" /> -->

        <ImprovedProductImage
          class="link p-2"
          tabindex="0"
          :src="getImageUrl(product.images ? product.images.mainImg : `${product.sku}.jpg`, 256)"
          :width="thumbnailSize"
          :height="thumbnailSize"
          square
        />
      </router-link>

      <div class="data">
        <div class="column minimalistic minimalistic-thin">
          <span
            v-if="store.showDiscreetAvailabilityStatuses"
            class="discreet-availability-status mr-1"
            :class="productAvailabilityCss"
            :title="product.availability?.label"
          />

          <MoreReadableSku
            :sku="product.sku"
            :is-temporary-sku="product.hasTemporarySku"
            :class="[{ 'preliminary-text': product?.propertyStatuses?.sku === 'preliminary' }, product.propertyStatuses?.sku]"
            :title="translate('Item No', 'attributes', 'capitalize')"
          />
          <img
            v-for="country of product.availableInCountries"
            :key="country"
            class="small-flag ml-1"
            :src="getCountryFlag(country)"
            :title="`${appTranslate('Product is available in %s', [translate(country, 'countries', 'capitalize')])}`"
          />

          <!-- <PropertyComponent
            v-if="propertiesByComparable.prefix"
            :property="propertiesByComparable.prefix"
            :product="product"
            :title="translate('Prefix', 'attributes', 'capitalize')"
            :show-if-contains="defaultPropertyVisibility"
          /> -->
          <PropertyComponent
            v-if="productName?.property"
            :property="productName.property"
            :product="product"
            :title="`${translate('Name', 'attributes', 'capitalize')} (${translate(productName?.language, 'languages')})`"
            class="flex-nowrap full-size"
            :show-if-contains="defaultPropertyVisibility"
          >
            <template v-if="!product.name && getMainName(product)" #before>
              <img class="language mr-1" :src="getFlag(undefined, productName.language)" />
            </template>
          </PropertyComponent>
          <template v-else>
            <div class="disabled font-italic">
              {{ appTranslate('Name missing') }}
            </div>
          </template>

          <PropertyComponent
            v-if="usedProperties.brand"
            :property="usedProperties.brand"
            :product="product"
            :title="translate('Brand', 'attributes', 'capitalize')"
            :show-if-contains="defaultPropertyVisibility"
          />
        </div>
        <div class="column minimalistic minimalistic-thin">
          <PropertyComponent
            v-if="usedProperties['barcode base unit']"
            :property="usedProperties['barcode base unit']"
            :product="product"
            always-show-label
            :show-if-contains="defaultPropertyVisibility"
          />

          <PropertyComponent
            v-for="(obj, i) of priceProperties"
            :key="i"
            class="price"
            :property="obj.property"
            :product="product"
            :title="obj?.property.info?.[store.infoLanguage]"
            :show-if-contains="defaultPropertyVisibility"
          >
            <template #before>
              <img :src="getFlag(obj.country)" class="tiny-flag mr-1" />
              <strong class="mr-1 label"> {{ translate('Recommended price incl VAT', 'attributes', 'capitalize') }}: </strong>
            </template>
          </PropertyComponent>
        </div>
        <div class="column minimalistic minimalistic-thin">
          <PropertyComponent
            v-for="(property, i) of shownAttributeProperties"
            :key="i"
            :property="property"
            :product="product"
            always-show-label
            :show-if-contains="defaultPropertyVisibility"
          />
        </div>
      </div>
    </template>

    <!-- Skeleton -->
    <template v-else>
      <div class="img-wrapper">
        <div class="main-img fill" />
      </div>
      <div class="data">
        <div class="column">
          <div class="fill" />
          <div class="fill" />
          <div class="fill" />
        </div>
        <div class="column">
          <div class="fill" />
          <div class="fill" />
        </div>
        <div class="column">
          <div class="fill" />
          <div class="fill" />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import ImprovedProductImage from '@/components/products/ImprovedProductImage.vue'
import PropertyComponent from '@/components/products/PropertyComponent.vue'
import MoreReadableSku from '@/components/simple/MoreReadableSkuComponent.vue'
import RibbonComponent from '@/components/simple/RibbonComponent.vue'
import { useProductFunctions } from '@/composables/useProduct'
import { useSelectedProperties } from '@/composables/useSelectedProperties'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { Country } from '@/models/Global'
import { SimplifiedProduct } from '@/models/Product'
import { EditableAdvancedProperty } from '@/models/Property'
import { useDefaultStore } from '@/store'
import { getImageUrl, getMainName } from '@/utils/productUtils'
import { getCountryFlag, mapArray } from '@/utils/tools'
import { computed, onMounted, toRef } from 'vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{ product?: SimplifiedProduct; showPlaceholder?: boolean; simple?: boolean }>()

const store = useDefaultStore()

const { translate, appTranslate } = useTranslation()
const { getFlag } = useTools()
const { productName, productAvailabilityCss } = useProductFunctions(toRef(props, 'product'))
const { initProperties, getPropertyByName, properties } = useSelectedProperties('products')

const thumbnailSize = '100px'

const defaultPropertyVisibility = 'value-or-preliminary'

// ---------------
// -- Reactives --
// ---------------
const shownAttributeProperties = computed(() => {
  if (!properties.value) {
    return
  }
  return mapArray(['supplier', 'operational buyer', 'remark purchase', 'remark sales', 'remark pu'], (item) => getPropertyByName(item))
})

const priceProperties = computed(() =>
  props.product?.availableInCountries?.reduce((acc: { property: EditableAdvancedProperty; country: Country }[], country) => {
    if (props.product?.countrySpecific?.[country]?.recommendedPriceInclVAT) {
      const property = getPropertyByName(`${country} recommended price including vat`)
      property && acc.push({ property, country })
    }
    return acc
  }, []),
)

const usedProperties = computed(() => ({
  // prefix: getPropertyByName('prefix'),
  brand: getPropertyByName('brand'),
  'barcode base unit': getPropertyByName('barcode base unit'),
}))

onMounted(() => {
  initProperties()
})
</script>

<style lang="scss" scoped>
@use '@/styles/skeleton.scss';

.product {
  // height: 300px;

  display: flex;
  flex-wrap: wrap;
  margin: 1em;
  position: relative;
  overflow: visible;

  // overflow-y: auto;
  // overflow-x: hidden;

  a {
    display: flex;
    // align-items: center;
  }

  h6 {
    font-size: 1.1em;
    font-weight: bold;
  }

  .data {
    display: grid;
    align-content: flex-start;
    flex-wrap: wrap;
    width: 100%;
    grid-template-columns: 1fr;
    grid-template-areas:
      'a'
      'b'
      'c';

    .column:nth-child(1) {
      grid-area: a;
    }
    .column:nth-child(2) {
      grid-area: b;
    }
    .column:nth-child(3) {
      grid-area: c;
    }

    .column {
      width: 100%;
      font-size: 80%;

      break-inside: avoid;
      break-after: auto;
      overflow-y: auto;
      // max-height: 100%;
      max-height: 100%;
      padding: 0.5em;

      .full-size {
        font-size: 1rem;
      }

      > * {
        vertical-align: middle;
      }
    }
  }

  @media (min-width: 400px) {
    flex-wrap: nowrap;
  }

  @media (min-width: 576px) {
    // height: 220px;

    .data {
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        'a b'
        'c b';
    }
  }

  @media (min-width: 768px) {
    // height: 120px;

    .data {
      grid-template-columns: repeat(3, 1fr);
      grid-template-areas: 'a b c';
    }
  }
}

.product.skeleton {
  .column .fill {
    margin-bottom: 5px;

    &:first-child {
      width: min(220px, 95%);
    }

    &:last-child {
      width: min(150px, 95%);
    }
  }

  .column:first-child .fill {
    &:first-child {
      width: min(110px, 95%);
    }

    &:nth-child(2) {
      width: min(180px, 95%);
      font-size: 24px;
    }

    &:last-child {
      width: min(70px, 95%);
    }
  }

  .img-wrapper {
    box-sizing: content-box;
    // min-width: 100px;
    // height: 100px;
    min-width: v-bind(thumbnailSize);
    height: v-bind(thumbnailSize);
  }
}
</style>
